import { useState, useEffect, useCallback, useMemo } from "react";
import { inventoryApi, type Product, type Model } from "../api/inventory";
import { salesApi } from "../api/sales";
import { settingsApi } from "../api/settings";
import { suppliesApi } from "../api/supplies";
import "./QuickSale.css";

type CartItem = Product & { modelCost: number; modelSellingPrice: number };

export default function QuickSale() {
  const [products, setProducts] = useState<Product[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [bagCost, setBagCost] = useState(0);
  const [cardCost, setCardCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Cart
  const [cart, setCart] = useState<CartItem[]>([]);

  // Form
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [totalTransferred, setTotalTransferred] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [saleComplete, setSaleComplete] = useState(false);
  const [menuCopied, setMenuCopied] = useState(false);
  const [homeAddress, setHomeAddress] = useState("");
  const [suggestedAddresses, setSuggestedAddresses] = useState<string[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [pRes, mRes, sRes, bRes, cRes] = await Promise.all([
      inventoryApi.getProducts(),
      inventoryApi.getModels(),
      settingsApi.get(),
      suppliesApi.getBags(),
      suppliesApi.getCards(),
    ]);
    if (pRes.error || mRes.error) {
      setError("Error loading data");
    } else {
      setProducts(pRes.data ?? []);
      setModels(mRes.data ?? []);
      if (sRes.data) setHomeAddress(sRes.data.homeAddress ?? "");
      if (bRes.data) setBagCost(bRes.data.weightedAvgCost);
      if (cRes.data) setCardCost(cRes.data.weightedAvgCost);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const modelMap = useMemo(() => {
    const map = new Map<number, Model>();
    models.forEach((m) => map.set(m.id, m));
    return map;
  }, [models]);

  // Available products (quantity > 0, not in cart)
  const getAvailableQty = (productId: number): number => {
    const product = products.find((p) => p.id === productId);
    if (!product) return 0;
    const inCart = cart.filter((c) => c.id === productId).length;
    return Math.max(0, product.quantity - inCart);
  };

  const availableProducts = products.filter((p) => getAvailableQty(p.id) > 0);

  // Cart totals
  const cartTotal = cart.reduce((sum, item) => sum + item.modelSellingPrice, 0);
  const cartVapeCost = cart.reduce((sum, item) => sum + item.modelCost, 0);
  const shippingNum = parseFloat(shippingCost) || 0;
  const netRevenue = cart.length > 0
    ? cartTotal - cartVapeCost - (cart.length * (bagCost + cardCost)) - shippingNum
    : 0;

  // ─── Handlers ──────────────────────────────────────

  const addToCart = (product: Product) => {
    if (getAvailableQty(product.id) <= 0) return;
    const model = modelMap.get(product.modelId);
    setCart((prev) => [
      ...prev,
      { ...product, modelCost: model?.costPrice ?? 0, modelSellingPrice: model?.sellingPrice ?? 0 },
    ]);
  };

  const removeFromCart = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRecordSale = async () => {
    if (cart.length === 0) return;
    setSaving(true);
    setError("");

    // Group cart items by productId
    const grouped = new Map<number, number>();
    cart.forEach((item) => {
      grouped.set(item.id, (grouped.get(item.id) ?? 0) + 1);
    });

    const items = Array.from(grouped.entries()).map(([productId, quantity]) => ({
      productId,
      quantity,
    }));

    const result = await salesApi.create({
      buyerPhone: phone,
      shippingAddress: address,
      totalTransferred: parseFloat(totalTransferred) || 0,
      shippingCostPaid: shippingNum,
      items,
    });

    if (result.error) {
      setError(result.error);
      setSaving(false);
    } else {
      setSaleComplete(true);
      setCart([]);
      setPhone("");
      setAddress("");
      setTotalTransferred("");
      setShippingCost("");
      await loadData(); // Refresh product quantities
      setSaving(false);
      setTimeout(() => setSaleComplete(false), 4000);
    }
  };

  const copyStockMenu = () => {
    const grouped = new Map<number, Product[]>();
    availableProducts.forEach((p) => {
      const list = grouped.get(p.modelId) ?? [];
      list.push(p);
      grouped.set(p.modelId, list);
    });

    let text = "*STOCK DISPONIBLE*\n\n";
    grouped.forEach((prods, modelId) => {
      const model = modelMap.get(modelId);
      text += `*${model?.name ?? "Modelo"}* — $${model?.sellingPrice ?? "?"}\n`;
      prods.forEach((p) => {
        text += `  - ${p.flavor} (${getAvailableQty(p.id)})\n`;
      });
      text += "\n";
    });
    text += "_Precios no incluyen envío._";

    navigator.clipboard.writeText(text).then(() => {
      setMenuCopied(true);
      setTimeout(() => setMenuCopied(false), 2000);
    });
  };

  // ─── Render ────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <div className="screen-header"><h1>Quick Sale</h1><p>Record a new sale in seconds</p></div>
        <p className="qs-status">Cargando productos…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="screen-header">
        <h1>Quick Sale</h1>
        <p>Record a new sale in seconds</p>
      </div>

      {error && (
        <div className="qs-error" onClick={() => setError("")}>
          Error: {error} <span className="qs-error__dismiss">x</span>
        </div>
      )}

      {saleComplete && (
        <div className="qs-success">Venta registrada exitosamente</div>
      )}

      {/* ─── Copy Stock Menu ─────────────────────── */}
      <button className="qs-menu-btn" onClick={copyStockMenu}>
        {menuCopied ? "¡Copiado!" : (
          <><span className="material-symbols-rounded">content_copy</span> Copy Stock Menu</>
        )}
      </button>

      {/* ─── Product Chips ───────────────────────── */}
      <section className="qs-section">
        <h3 className="qs-section__title">
          Productos disponibles ({availableProducts.length})
        </h3>
        <div className="qs-chips">
          {availableProducts.map((p) => {
            const model = modelMap.get(p.modelId);
            const qty = getAvailableQty(p.id);
            return (
              <button
                key={p.id}
                className="qs-chip"
                onClick={() => addToCart(p)}
              >
                <span className="qs-chip__flavor">{p.flavor}</span>
                <span className="qs-chip__model">{model?.name}</span>
                <span className="qs-chip__price">${model?.sellingPrice}</span>
                <span className="qs-chip__qty">{qty}</span>
              </button>
            );
          })}
          {availableProducts.length === 0 && (
            <p className="qs-empty">Sin stock disponible. Agrega productos en Inventory.</p>
          )}
        </div>
      </section>

      {/* ─── Cart ────────────────────────────────── */}
      <section className="qs-section">
        <h3 className="qs-section__title">
          <span className="material-symbols-rounded">shopping_cart</span> Carrito ({cart.length}) — Total: ${cartTotal}
        </h3>
        {cart.length === 0 ? (
          <p className="qs-empty">Carrito vacío</p>
        ) : (
          <div className="qs-chips">
            {cart.map((item, i) => (
              <button
                key={`${item.id}-${i}`}
                className="qs-chip qs-chip--cart"
                onClick={() => removeFromCart(i)}
              >
                <span className="qs-chip__flavor">{item.flavor}</span>
                <span className="qs-chip__price">${item.modelSellingPrice}</span>
                <span className="qs-chip__remove">✕</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ─── Sale Form ───────────────────────────── */}
      <section className="qs-section">
        <h3 className="qs-section__title">Datos de la venta</h3>

        <label className="qs-field">
          <span>
            <span className="material-symbols-rounded">call</span> Teléfono del comprador</span>
          <input
            className="qs-input"
            type="tel"
            placeholder="+5215551234567"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              const phoneVal = e.target.value.trim();
              if (phoneVal.length >= 10) {
                salesApi.getAll().then((res) => {
                  if (res.data && Array.isArray(res.data)) {
                    const matches = res.data
                      .filter((s) => s.buyerPhone.includes(phoneVal) && s.shippingAddress)
                      .map((s) => s.shippingAddress);
                    setSuggestedAddresses([...new Set(matches)].slice(0, 3));
                  }
                }).catch(() => {});
              } else {
                setSuggestedAddresses([]);
              }
            }}
            disabled={saving}
          />
        </label>

        {/* Address suggestions */}
        {suggestedAddresses.length > 0 && (
          <div className="qs-suggestions">
            {suggestedAddresses.map((addr, i) => (
              <button
                key={i}
                className="qs-suggestion-chip"
                onClick={() => { setAddress(addr); setSuggestedAddresses([]); }}
                type="button"
              >
                {addr.length > 50 ? addr.slice(0, 50) + "…" : addr}
              </button>
            ))}
          </div>
        )}

        <label className="qs-field">
          <span>
            <span className="material-symbols-rounded">location_on</span> Dirección de entrega</span>
          <textarea
            className="qs-input qs-textarea"
            placeholder="Dirección o link de Google Maps"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            disabled={saving}
          />
        </label>

        {/* Uber deep link */}
        {homeAddress && address && (
          <a
            href={`uber://?action=setPickup&pickup[formatted_address]=${encodeURIComponent(homeAddress)}&dropoff[formatted_address]=${encodeURIComponent(address)}`}
            className="qs-uber-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="material-symbols-rounded">directions_car</span> Abrir en Uber
          </a>
        )}

        <div className="qs-form-row">
          <label className="qs-field">
            <span>
              <span className="material-symbols-rounded">payments</span> Total transferido ($)</span>
            <input
              className="qs-input"
              type="number"
              placeholder="Total transferido"
              value={totalTransferred}
              onChange={(e) => setTotalTransferred(e.target.value)}
              min={0}
              disabled={saving}
            />
          </label>

          <label className="qs-field">
            <span>
              <span className="material-symbols-rounded">local_shipping</span> Costo de envío pagado ($)</span>
            <input
              className="qs-input"
              type="number"
              placeholder="Costo de envío"
              value={shippingCost}
              onChange={(e) => setShippingCost(e.target.value)}
              min={0}
              disabled={saving}
            />
          </label>
        </div>

        {/* ─── Net Revenue Preview ──────────────── */}
        {cart.length > 0 && (
          <div className="qs-net">
            <span className="qs-net__label">Ganancia estimada:</span>
            <span className={`qs-net__value ${netRevenue >= 0 ? "qs-net--positive" : "qs-net--negative"}`}>
              ${netRevenue.toFixed(2)}
            </span>
            <span className="qs-net__detail">
              Venta: ${cartTotal} − Costo: ${cartVapeCost} − Envío: ${shippingNum}{bagCost + cardCost > 0 ? ` − Bolsa+Tarjeta: $${((bagCost + cardCost) * cart.length).toFixed(2)}` : ""}
            </span>
          </div>
        )}

        <button
          className="qs-submit"
          onClick={handleRecordSale}
          disabled={saving || cart.length === 0 || !phone.trim()}
        >
          {saving ? "Registrando…" : (
            <><span className="material-symbols-rounded">receipt_long</span> Registrar venta</>
          )}
        </button>
      </section>
    </div>
  );
}
